import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMap,
  faPercent,
  faGlobe,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import api from '../lib/api';
import { logger } from '../lib/logger';
import ExplorationMap from '../components/explore/ExplorationMap';
import { buildExploredData } from '../components/explore/FogOverlay';

const EARTH_LAND_AREA_KM2 = 148_940_000;

function toGeoJSON(coord) {
  if (coord.latitude != null) return [coord.longitude, coord.latitude];
  return coord;
}

/**
 * Extracts one representative [lng, lat] per route, deduplicates by a
 * coarse grid (~110 km cells), and reverse-geocodes each unique cell
 * via Nominatim to find country names.
 */
async function detectCountries(routes) {
  if (!routes || routes.length === 0) return [];

  const GRID_DEG = 1;
  const cellMap = new Map();

  for (const route of routes) {
    const rd = route.route_data;
    const raw = rd?.startLocation || rd?.coordinates?.[0];
    if (!raw) continue;
    const [lng, lat] = toGeoJSON(raw);
    const key = `${Math.round(lat / GRID_DEG)},${Math.round(lng / GRID_DEG)}`;
    if (!cellMap.has(key)) {
      cellMap.set(key, { lat, lng });
    }
  }

  const countries = new Set();

  for (const { lat, lng } of cellMap.values()) {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=3&accept-language=en`,
        { headers: { 'User-Agent': 'ZoneRuns/1.0' } }
      );
      if (!res.ok) continue;
      const data = await res.json();
      const country = data.address?.country;
      if (country) countries.add(country);
    } catch {
      // skip failed lookups
    }
    // respect Nominatim 1-req/sec policy
    await new Promise((r) => setTimeout(r, 1100));
  }

  return Array.from(countries).sort();
}

const Explore = () => {
  const [routes, setRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const isMountedRef = useRef(true);

  const fetchRoutes = useCallback(async () => {
    try {
      const data = await api.runs.listRoutes();
      if (isMountedRef.current) {
        setRoutes(data || []);
      }
    } catch (error) {
      logger.warn('Error fetching routes:', error);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchRoutes();
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchRoutes]);

  useEffect(() => {
    if (routes.length === 0) {
      setCountries([]);
      return;
    }

    let cancelled = false;
    setCountriesLoading(true);

    detectCountries(routes).then((result) => {
      if (!cancelled) {
        setCountries(result);
        setCountriesLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [routes]);

  const stats = useMemo(() => {
    const { exploredArea } = buildExploredData(routes);
    const areaKm2 = exploredArea / 1_000_000;
    const pct = (areaKm2 / EARTH_LAND_AREA_KM2) * 100;
    return { percentDiscovered: pct };
  }, [routes]);

  const formatPercent = (pct) => {
    if (pct === 0) return '0.000%';
    if (pct < 0.001) return pct.toFixed(6) + '%';
    if (pct < 1) return pct.toFixed(3) + '%';
    return pct.toFixed(2) + '%';
  };

  if (isLoading) {
    return (
      <div className="p-4 max-w-6xl mx-auto flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-primary-pink animate-spin mb-4" />
          <p className="text-gray-600">Loading exploration map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="mb-4">
        <h1 className="text-3xl font-heading font-semibold mb-1 bg-gradient-vibe bg-clip-text text-transparent">
          Exploration Map
        </h1>
        <p className="text-gray-600 text-body">
          Discover how much of the world you've unlocked through running
        </p>
      </div>

      <div className="relative rounded-2xl overflow-hidden shadow-lg border border-subtle-border" style={{ height: 'calc(100vh - 220px)' }}>
        <ExplorationMap routes={routes} />

        <div className="absolute top-4 right-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-5 min-w-[260px]">
          <h3 className="font-heading font-semibold text-sm text-gray-900 mb-4 flex items-center gap-2">
            <FontAwesomeIcon icon={faMap} className="w-4 h-4 text-primary-pink" />
            Exploration Stats
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 flex items-center gap-1.5">
                <FontAwesomeIcon icon={faPercent} className="w-3 h-3" />
                Discovered
              </span>
              <span className="font-semibold text-gray-900">
                {formatPercent(stats.percentDiscovered)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 flex items-center gap-1.5">
                <FontAwesomeIcon icon={faGlobe} className="w-3 h-3" />
                Countries
              </span>
              <span className="font-semibold text-gray-900">
                {countriesLoading ? (
                  <FontAwesomeIcon icon={faSpinner} className="w-3 h-3 animate-spin" />
                ) : (
                  countries.length
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Explore;
